import React, { useState } from "react";
import { ArrowRight, Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { createCheckoutSession } from "../lib/api";

const STATIC_PLANS = [
  {
    plan_id: "free",
    name: "Free",
    amount: 0,
    cadence: "forever",
    tagline: "Entry-level DevOps Lab access",
    features: [
      "Limited DevOps Lab dashboards",
      "Public read-only tools",
      "Community support",
    ],
    cta_label: "Get started",
    cta_kind: "free",
    highlighted: false,
  },
  {
    plan_id: "pro",
    name: "Pro",
    amount: 39,
    cadence: "per month",
    tagline: "Full DevOps Lab + Terraform + Kubernetes",
    features: [
      "Full DevOps Lab access",
      "Terraform Dashboard",
      "Kubernetes Lab",
      "CI/CD tools",
      "Email support",
    ],
    cta_label: "Subscribe",
    cta_kind: "stripe",
    highlighted: true,
  },
  {
    plan_id: "ai_voice",
    name: "AI Voice",
    amount: 69,
    cadence: "per month",
    tagline: "Pro + Voice + AIOps",
    features: [
      "Everything in Pro",
      "Speechify voice integration",
      "AI summaries on every page",
      "AIOps insights dashboard",
      "Priority support",
    ],
    cta_label: "Subscribe",
    cta_kind: "stripe",
    highlighted: false,
  },
  {
    plan_id: "enterprise",
    name: "Enterprise",
    amount: null,
    cadence: "custom",
    tagline: "Custom deployments & team features",
    features: [
      "Everything in AI Voice",
      "Team accounts & SSO",
      "Dedicated cloud architect",
      "Custom SLAs & invoicing",
    ],
    cta_label: "Contact Sales",
    cta_kind: "contact",
    highlighted: false,
  },
];

export default function Pricing({ compact = false }) {
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleSubscribe = async (plan) => {
    setLoadingPlan(plan.plan_id);
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const { url } = await createCheckoutSession({
        plan_id: plan.plan_id,
        origin_url: origin,
      });
      // Track conversion intent
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "begin_checkout", {
          event_category: "pricing",
          event_label: plan.plan_id,
          value: plan.amount,
        });
      }
      window.location.href = url;
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail ||
          "Unable to start checkout. Please try again."
      );
      setLoadingPlan(null);
    }
  };

  const handleFree = () => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "sign_up_free", {
        event_category: "pricing",
        event_label: "free",
      });
    }
    toast.success("You're on the Free tier. Explore the DevOps Lab.");
    setTimeout(() => {
      window.location.href = "/devops-tools";
    }, 700);
  };

  const handleContactSales = () => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "contact_sales_click", {
        event_category: "pricing",
        event_label: "enterprise",
      });
    }
    window.location.href = "/#contact";
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className={`nst-light ${compact ? "py-20 lg:py-24" : "py-24 lg:py-32"}`}
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="max-w-2xl">
          <span className="pill-label">Pricing</span>
          <h2 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#0A1F47] leading-[1.05]">
            Pick the tier that <span className="text-[#2E6BF6]">ships your platform</span>
          </h2>
          <p className="mt-5 text-base md:text-lg text-[#0A1F47]/70 leading-relaxed">
            Start free, scale as your team grows. Cancel anytime. USD billing, secured by Stripe.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STATIC_PLANS.map((plan) => {
            const isLoading = loadingPlan === plan.plan_id;
            const highlighted = plan.highlighted;
            return (
              <div
                key={plan.plan_id}
                data-testid={`pricing-card-${plan.plan_id}`}
                className={`relative flex flex-col rounded-2xl border p-7 transition-all duration-300 ${
                  highlighted
                    ? "bg-[#0A1F47] text-white border-[#0A1F47] shadow-xl lg:-translate-y-3"
                    : "bg-white text-[#0A1F47] border-[#E2E8F4] hover:border-[#2E6BF6] hover:shadow-lg"
                }`}
              >
                {highlighted && (
                  <div className="absolute -top-3 left-7 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#16C47F] text-white text-xs font-bold tracking-wide">
                    <Sparkles className="w-3 h-3" /> MOST POPULAR
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-extrabold">{plan.name}</h3>
                  <p
                    className={`mt-1 text-sm ${
                      highlighted ? "text-white/70" : "text-[#0A1F47]/60"
                    }`}
                  >
                    {plan.tagline}
                  </p>
                </div>
                <div className="mt-6">
                  {plan.amount === 0 ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold">$0</span>
                      <span
                        className={`text-sm ${
                          highlighted ? "text-white/60" : "text-[#0A1F47]/60"
                        }`}
                      >
                        / {plan.cadence}
                      </span>
                    </div>
                  ) : plan.amount == null ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold">Custom</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-4xl font-extrabold">
                        ${plan.amount}
                      </span>
                      <span
                        className={`text-sm ${
                          highlighted ? "text-white/60" : "text-[#0A1F47]/60"
                        }`}
                      >
                        / {plan.cadence}
                      </span>
                    </div>
                  )}
                </div>
                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check
                        className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                          highlighted ? "text-[#16C47F]" : "text-[#2E6BF6]"
                        }`}
                      />
                      <span
                        className={
                          highlighted ? "text-white/85" : "text-[#0A1F47]/80"
                        }
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  {plan.cta_kind === "free" && (
                    <button
                      data-testid={`subscribe-btn-${plan.plan_id}`}
                      onClick={handleFree}
                      className="w-full inline-flex justify-center items-center gap-2 py-3 rounded-xl bg-[#F5F7FA] hover:bg-[#E2E8F4] text-[#0A1F47] font-semibold transition-colors"
                    >
                      {plan.cta_label} <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                  {plan.cta_kind === "stripe" && (
                    <button
                      data-testid={`subscribe-btn-${plan.plan_id}`}
                      onClick={() => handleSubscribe(plan)}
                      disabled={isLoading}
                      className={`w-full inline-flex justify-center items-center gap-2 py-3 rounded-xl font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                        highlighted
                          ? "bg-[#2E6BF6] hover:bg-[#1E55D6] text-white"
                          : "bg-[#0A1F47] hover:bg-[#0A1F47]/90 text-white"
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                        </>
                      ) : (
                        <>
                          {plan.cta_label} <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  )}
                  {plan.cta_kind === "contact" && (
                    <button
                      data-testid={`subscribe-btn-${plan.plan_id}`}
                      onClick={handleContactSales}
                      className="w-full inline-flex justify-center items-center gap-2 py-3 rounded-xl bg-white border-2 border-[#0A1F47] hover:bg-[#0A1F47] hover:text-white text-[#0A1F47] font-semibold transition-colors"
                    >
                      {plan.cta_label} <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-xs text-[#0A1F47]/50 text-center">
          Secured by Stripe · USD · cancel anytime · no setup fees
        </p>
      </div>
    </section>
  );
}
